
import DialogTemplate from '@/_components/dialog/template/template.vue'
import { cloneDeep, debounce } from 'lodash'
import { accessClient } from '@/apolloClient'
import RuleDialog from './ruleDialog.vue'
import { openDialog } from '@/_components/dialog/dialog'
import ReaderDialog from './readerDialog.vue'
import SaveStatusIndicator from '@/components/saveStatusIndicator.vue'
import {
    UPDATE_ACCESS_GROUP_NAME,
    UPDATE_ACCESS_GROUP_DISABLE_ANTI_PASSBACK,
    UPDATE_ACCESS_GROUP_GRANT_WHEN_PSI_COMPLETED,
    UPDATE_ACCESS_GROUP_IGNORE_GEOFENCE_WHEN_OPENING_WITH_APP,
    CREATE_ACCESS_GROUP_READER_RULE_ASSOCIATION,
    DELETE_ACCESS_GROUP_READER_RULE_ASSOCIATION,
    SET_ACCESS_GROUP_READER_RULE_ASSOCIATION_CAN_BE_OPENED_WITH_APP,
    SET_ACCESS_GROUP_READER_RULE_ASSOCIATION_REQUIRES_PIN,
} from './gql/mutations'

export default {
    name: 'AccessControlGroupDialog',
    components: { DialogTemplate, SaveStatusIndicator },
    props: {
        props: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            group: cloneDeep(this.props.group),
            updatedGroup: null as Maybe<any>,
            ruleReaderUpdated: false,
            saveStatus: null as Maybe<'saved' | 'saving'>,
            readerRules: {} as any,
            rules: cloneDeep(this.props.rules),
        }
    },
    computed: {
        readerById() {
            const items = {}
            this.props.readers?.forEach((reader) => {
                items[reader.readerId] = reader
            })
            return items
        },
        isDisabled() {
            return Boolean(this.props.disabled)
        },
        isSettingGroupRoute() {
            return this.$route.name === 'access-settings-groups'
        },
    },
    methods: {
        async setGroupFeature(
            featureKey:
                | 'disableAntiPassback'
                | 'grantWhenPsiCompleted'
                | 'ignoreGeofenceWhenOpeningWithApp',
            value: boolean
        ) {
            let mutation
            switch (featureKey) {
                case 'disableAntiPassback':
                    mutation = UPDATE_ACCESS_GROUP_DISABLE_ANTI_PASSBACK
                    break
                case 'grantWhenPsiCompleted':
                    mutation = UPDATE_ACCESS_GROUP_GRANT_WHEN_PSI_COMPLETED
                    break
                case 'ignoreGeofenceWhenOpeningWithApp':
                    mutation = UPDATE_ACCESS_GROUP_IGNORE_GEOFENCE_WHEN_OPENING_WITH_APP
                    break
            }
            const operationName = mutation.definitions[0].name.value

            try {
                this.saveStatus = 'saving'
                const result = await accessClient.mutate({
                    mutation,
                    variables: { groupId: this.group.id, value },
                })

                this.saveStatus = 'saved'
                setTimeout(() => {
                    this.saveStatus = null
                }, 2000)
                this.updatedGroup = cloneDeep(result.data[operationName].group)
            } catch (e) {
                this.saveStatus = null
                throw e
            }
        },
        setGroupName: debounce(async function () {
            this.saveStatus = 'saving'
            try {
                const ruleResult = await accessClient.mutate({
                    mutation: UPDATE_ACCESS_GROUP_NAME,
                    variables: {
                        groupId: this.group.id,
                        name: this.group.name,
                    },
                })
                this.saveStatus = 'saved'
                setTimeout(() => {
                    this.saveStatus = null
                }, 2000)
                this.updatedGroup = cloneDeep(ruleResult.data.setAccessGroupName.group)
            } catch (e) {
                this.saveStatus = null
                throw e
            }
        }, 300),
        async openRule(rule: any) {
            if (rule.type !== 'REGULAR' || this.isDisabled || !this.isSettingGroupRoute) {
                return
            }
            const dialog = await openDialog(RuleDialog, { rule })
            if (dialog?.deletedAt) {
                this.rules = this.rules.filter((_rule) => _rule.id !== rule.id)
            } else if (dialog?.updatedRule) {
                this.rules = this.rules.map((_rule) => {
                    if (_rule.id === rule.id) {
                        return { ...dialog.updatedRule }
                    }
                    return _rule
                })
            }
        },
        async openReader(readerId: string) {
            if (this.isDisabled || !this.isSettingGroupRoute) {
                return
            }

            const reader = this.props.readers.find((r) => r.readerId === readerId)
            if (!reader) {
                return
            }
            await openDialog(ReaderDialog, { reader })
        },
        async updateAssociation(readerId: string, ruleId: string, isSet: boolean) {
            const mutation = isSet
                ? CREATE_ACCESS_GROUP_READER_RULE_ASSOCIATION
                : DELETE_ACCESS_GROUP_READER_RULE_ASSOCIATION

            try {
                this.saveStatus = 'saving'
                const result = await accessClient.mutate({
                    mutation,
                    variables: {
                        groupId: this.group.id,
                        readerId,
                        ruleId,
                    },
                })
                const operationName = mutation.definitions[0].name.value
                if (result.data[operationName].errors) {
                    this.readerRules[readerId]._rules[ruleId] =
                        !this.readerRules[readerId]._rules[ruleId]
                    this.showErrors(
                        result.data[operationName].errors
                            .map((error: any) => error.message)
                            .join('\n')
                    )
                    throw new Error(
                        result.data[operationName].errors
                            .map((error: any) => error.message)
                            .join('\n')
                    )
                }
                this.saveStatus = 'saved'
                setTimeout(() => {
                    this.saveStatus = null
                }, 2000)
            } catch (e) {
                this.saveStatus = null
                throw e
            }
            this.ruleReaderUpdated = true
        },
        async setRuleReaderFeature(
            readerId: string,
            ruleId: string,
            featureKey: 'canBeOpenedWithApp' | 'requiresPin',
            value: boolean
        ) {
            let mutation
            switch (featureKey) {
                case 'canBeOpenedWithApp':
                    mutation = SET_ACCESS_GROUP_READER_RULE_ASSOCIATION_CAN_BE_OPENED_WITH_APP
                    break
                case 'requiresPin':
                    mutation = SET_ACCESS_GROUP_READER_RULE_ASSOCIATION_REQUIRES_PIN
                    break
            }

            try {
                this.saveStatus = 'saving'
                await accessClient.mutate({
                    mutation,
                    variables: {
                        groupId: this.group.id,
                        readerId,
                        ruleId,
                        value,
                    },
                })
                this.saveStatus = 'saved'
                setTimeout(() => {
                    this.saveStatus = null
                }, 2000)
                this.ruleReaderUpdated = true
            } catch (e) {
                this.saveStatus = null
                throw e
            }
        },
        closeDialog() {
            this.$emit('close', {
                updatedGroup: this.updatedGroup,
                ruleReaderUpdated: this.ruleReaderUpdated,
            })
        },
        closeDialogAndGoToGroups() {
            this.closeDialog()
            this.$router.push({ name: 'access-settings-groups' })
        },
        showErrors(message: string) {
            this.$store.commit('setSuccessSnackBar', { subtitle: message, timeout: 5000 })
        },
    },
    mounted() {
        this.props.readers.forEach((reader) => {
            this.$set(this.readerRules, reader.readerId, {
                _rules: {},
                _canBeOpenedWithApp: {},
                _requiresPin: {},
            })
        })
        this.props.readerRules?.forEach((rule) => {
            if (this.readerRules[rule.readerId]) {
                this.readerRules[rule.readerId]._rules[rule.ruleId] = true
                this.readerRules[rule.readerId]._canBeOpenedWithApp[rule.ruleId] =
                    rule.canBeOpenedWithApp
            }
        })
    },
}
